<template>
  <div class="SeatMapUnit" :class="`SeatMapUnit--${type}`" :data-unit-type="type">
    <span v-if="type === 'info' || type === 'aisle'" class="SeatMapUnit-text">{{ text }}</span>
    <Icon
      v-else-if="type === 'exit'"
      :icon="direction === 'left' ? 'arrow-left' : 'arrow-right'"
      size="sm"
      class="SeatMapUnit-exitIcon"
    />
    <template v-else-if="type !== 'table'">
      <Icon :icon="UNIT_ICONS[type]" size="sm" class="SeatMapUnit-icon" />
      <span class="SeatMapUnit-label">{{ t(`ui.components.seatMap.units.${type}`) }}</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Icon from '../../atoms/Icon/Icon.vue';
import type { IconName } from '../../atoms/Icon/Icon.model';
import type { SeatMapUnitDirection, SeatMapUnitType } from './SeatMap.model';

defineProps<{
  type: Exclude<SeatMapUnitType, 'seat'>;
  direction?: SeatMapUnitDirection;
  /** Raw display text for `info`/`aisle` units (column letters, row numbers…), taken verbatim from the schema. */
  text?: string;
}>();

const { t } = useI18n();

type UnitWithIcon = Extract<SeatMapUnitType, 'wc' | 'galley' | 'luggage' | 'stairs'>;
const UNIT_ICONS: Record<UnitWithIcon, IconName> = {
  wc: 'human-male-female',
  galley: 'coffee',
  luggage: 'bag-suitcase',
  stairs: 'stepper',
};
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .SeatMapUnit {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: vars.$dimensions-d-0-5;

    &--wc,
    &--galley,
    &--luggage,
    &--stairs {
      border-width: vars.$atoms-seatcell-other-border-stroke;
      border-style: solid;
      border-color: vars.$atoms-seatcell-other-enabled-border-color;
      border-radius: vars.$atoms-seatcell-other-border-radius;
      background-color: vars.$atoms-seatcell-other-enabled-bg-color;
      padding: vars.$dimensions-d-2;

      .SeatMapUnit-icon {
        color: vars.$atoms-seatcell-other-enabled-icon-color;
      }

      .SeatMapUnit-label {
        color: vars.$atoms-seatcell-other-enabled-text-color;
        @include text.text('xs', vars.$typography-weight-strong);
      }
    }

    &--table {
      width: 100%;
      height: 100%;
      min-height: 32px;
      border-width: vars.$atoms-seatcell-table-border-stroke;
      border-style: solid;
      border-color: vars.$atoms-seatcell-table-enabled-border-color;
      border-radius: vars.$atoms-seatcell-table-border-radius;
      background-color: vars.$atoms-seatcell-table-enabled-bg-color;
    }

    // No dedicated design token for these two — they're decorative schema-driven text/icons,
    // not seats, so they borrow the closest existing semantic tokens.
    &--exit {
      .SeatMapUnit-exitIcon {
        color: vars.$color-system-status-success-900;
      }
    }

    &--info,
    &--aisle {
      .SeatMapUnit-text {
        color: vars.$color-neutral-gray-700;
        @include text.text('xs', vars.$typography-weight-strong);
      }
    }

    // The aisle gap is narrower than a seat cell; it only widens enough to show a row number.
    &--aisle {
      min-width: vars.$dimensions-d-5;
    }
  }
}
</style>
